<template>
  <div class="reset-container">
    <h1>{{ $t("FormPasswordResetPassword.title") }}</h1>
    <el-form ref="form" :model="form" label-position="top" class="reset-form">
      <el-form-item>
        <template #label>
          <label>
            {{ $t("FormPasswordResetPassword.password") }}
            <el-input
              v-model="password"
              :placeholder="$t('FormPasswordResetPassword.password')"
              type="password"
            />
          </label>
        </template>
      </el-form-item>
      <el-form-item :error="!passwordsMatch && passwordConfirm ? $t('FormPasswordResetPassword.passwordsDoNotMatch') : ''">
        <template #label>
          <label>
            {{ $t("FormPasswordResetPassword.passwordConfirm") }}
            <el-input
              v-model="passwordConfirm"
              :placeholder="$t('FormPasswordResetPassword.passwordConfirm')"
              type="password"
            />
          </label>
        </template>
      </el-form-item>
      <div class="password-requirements">
        <div class="requirement" :class="{ 'is-valid': password.length >= 10 }">
          <span class="icon">{{ password.length >= 10 ? '✓' : '✗' }}</span>
          {{ $t('FormPasswordResetPassword.minLength') }}
        </div>
        <div class="requirement" :class="{ 'is-valid': hasSpecialChar }">
          <span class="icon">{{ hasSpecialChar ? '✓' : '✗' }}</span>
          {{ $t('FormPasswordResetPassword.specialChar') }}
        </div>
        <div class="requirement" :class="{ 'is-valid': hasCase }">
          <span class="icon">{{ hasCase ? '✓' : '✗' }}</span>
          {{ $t('FormPasswordResetPassword.case') }}
        </div>
      </div>
      <div class="flex justify-start mt-8">
        <el-button
          class="has-icon"
          :disabled="!isPasswordValid || !passwordsMatch"
          size="default"
          @click="resetPassword"
        >
          <SvgIcon name="check-circle" />
          {{ $t("FormPasswordResetPassword.button") }}
        </el-button>
      </div>
    </el-form>
  </div>
</template>
<script setup>
import { ref, computed, h } from "vue";
import { useUserStore } from "@/store/User";
import { ElNotification } from "element-plus";
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['success']);
const userStore = useUserStore();

const password = ref("");
const passwordConfirm = ref("");
const { t } = useI18n();

const hasSpecialChar = computed(() => {
  return /[^A-Za-z0-9]/.test(password.value);
});

const hasCase = computed(() => {
  return /[a-z]/.test(password.value) && /[A-Z]/.test(password.value);
});

const isPasswordValid = computed(() => {
  return password.value.length >= 10 && hasSpecialChar.value && hasCase.value;
});

const passwordsMatch = computed(() => {
  return password.value === passwordConfirm.value;
});

const props = defineProps({
  token: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    required: true,
  },
});

const form = ref({
  password: '',
  passwordConfirm: '',
});

const resetPassword = async () => {
    await userStore.resetPassword(props.email, password.value, props.token);
    ElNotification({
    title: t("FormPasswordResetPassword.successTitle"),
    message: h(
      "div",
      { style: "color: teal; white-space: pre-line" },
      t("FormPasswordResetPassword.successText")
    ),
  });
    emit('success');
};
</script>

<style lang="postcss">
.login-container {
  @apply w-full;
}

@screen md {
  .login-container {
    @apply w-1/2 mx-auto;
  }
}

.login-form {
  @apply w-full;
}

@screen md {
  .login-form {
    @apply w-full;
  }
}

.password-requirements {
  margin-top: 2rem;
  font-size: 0.875rem;
  
  .requirement {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    color: #666;
    
    &.is-valid {
      color: #4CAF50;
    }
    
    .icon {
      margin-right: 0.5rem;
      font-weight: bold;
    }
  }
}
</style>
